<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const verticalOptions = [
  'UNDEFINED',
  'OTHER',
  'AUTO',
  'BEAUTY',
  'APPAREL',
  'EDU',
  'ENTERTAIN',
  'EVENT_PLAN',
  'FINANCE',
  'GROCERY',
  'GOVT',
  'HOTEL',
  'HEALTH',
  'NONPROFIT',
  'PROF_SERVICES',
  'RETAIL',
  'TRAVEL',
  'RESTAURANT',
];

const isLoading = ref(false);
const isSaving = ref(false);
const hasLoaded = ref(false);
const loadError = ref('');
const initialProfileSnapshot = ref('');
const websitesText = ref('');
const profile = ref({
  about: '',
  address: '',
  description: '',
  email: '',
  vertical: '',
});

const normalizedProfilePayload = computed(() => ({
  about: profile.value.about.trim(),
  address: profile.value.address.trim(),
  description: profile.value.description.trim(),
  email: profile.value.email.trim(),
  vertical: profile.value.vertical.trim(),
  websites: websitesText.value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean),
}));

const canSave = computed(() => {
  return (
    !isLoading.value &&
    JSON.stringify(normalizedProfilePayload.value) !==
      initialProfileSnapshot.value
  );
});

const hydrateProfile = payload => {
  profile.value = {
    about: payload?.about || '',
    address: payload?.address || '',
    description: payload?.description || '',
    email: payload?.email || '',
    vertical: payload?.vertical || '',
  };
  websitesText.value = Array(payload?.websites || []).join('\n');
  initialProfileSnapshot.value = JSON.stringify({
    about: profile.value.about.trim(),
    address: profile.value.address.trim(),
    description: profile.value.description.trim(),
    email: profile.value.email.trim(),
    vertical: profile.value.vertical.trim(),
    websites: websitesText.value
      .split('\n')
      .map(item => item.trim())
      .filter(Boolean),
  });
};

const loadProfile = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppProfile(props.inbox.id);
    hydrateProfile(response.data?.payload || {});
    loadError.value = '';
    hasLoaded.value = true;
  } catch (error) {
    loadError.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_LOAD_ERROR');
    useAlert(loadError.value);
  } finally {
    isLoading.value = false;
  }
};

const saveProfile = async () => {
  isSaving.value = true;
  try {
    const response = await InboxesAPI.updateWhatsAppProfile(
      props.inbox.id,
      normalizedProfilePayload.value
    );
    hydrateProfile(response.data?.payload || {});
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

watch(
  () => props.active,
  isActive => {
    if (isActive && !hasLoaded.value) loadProfile();
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_NOTE') }}
      </p>
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-ph-arrows-clockwise"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_REFRESH')"
        :is-loading="isLoading"
        @click="loadProfile"
      />
    </div>

    <div
      v-if="isLoading"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_LOADING') }}
    </div>

    <div
      v-else-if="loadError"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-6 text-sm text-n-ruby-11"
    >
      {{ loadError }}
    </div>

    <div v-else class="grid gap-4 rounded-xl border border-n-container bg-n-alpha-2 p-4">
      <div class="grid gap-4 lg:grid-cols-2">
        <woot-input
          v-model="profile.about"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_ABOUT')"
        />
        <woot-input
          v-model="profile.email"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_EMAIL')"
        />
        <woot-input
          v-model="profile.address"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_ADDRESS')"
        />
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          <span class="font-medium">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_VERTICAL') }}
          </span>
          <select
            v-model="profile.vertical"
            class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option
              v-for="option in verticalOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
          </select>
        </label>
      </div>

      <TextArea
        v-model="profile.description"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_DESCRIPTION')"
        auto-height
        min-height="5rem"
      />

      <TextArea
        v-model="websitesText"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_WEBSITES')"
        :placeholder="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_WEBSITES_PLACEHOLDER')
        "
        auto-height
        min-height="5rem"
      />

      <div class="flex justify-end">
        <Button
          type="button"
          color="blue"
          icon="i-ph-floppy-disk"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_SAVE')"
          :disabled="!canSave"
          :is-loading="isSaving"
          @click="saveProfile"
        />
      </div>
    </div>
  </div>
</template>
